import { motion } from "framer-motion";
import { Brain, Cpu, Server, Database, BarChart3, Wrench } from "lucide-react";
import { techStack, accentTokens } from "../data/mock";

const iconFor = (name) => {
  switch (name) {
    case "AI / LLM Systems":
      return Brain;
    case "Machine Learning":
      return Cpu;
    case "Data Science & Analytics":
      return BarChart3;
    case "Backend & Deployment":
      return Server;
    case "Data & Infrastructure":
      return Database;
    case "Tooling & Collaboration":
      return Wrench;
    default:
      return Brain;
  }
};

export const TechStack = () => {
  return (
    <section
      id="stack"
      className="relative py-24 md:py-32"
      data-testid="tech-stack-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
            // 04 — Technical Stack
          </p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            The tools I{" "}
            <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
              reach for.
            </span>
          </h2>
          <p className="mt-4 max-w-2xl text-cyan-100/70 text-base sm:text-lg leading-relaxed">
            Production-tested across LLM serving, classical ML, deployment, and
            data infrastructure.
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {techStack.map((cat, idx) => {
            const Icon = iconFor(cat.category);
            const t = accentTokens[cat.accent] || accentTokens.cyan;
            return (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.6,
                  delay: idx * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`relative group gradient-border rounded-2xl glass p-6 sm:p-7 hover:-translate-y-1 transition-transform duration-300 ${t.glow}`}
                data-testid={`tech-category-${idx}`}
              >
                <div className={`absolute -top-10 -right-10 h-32 w-32 ${t.blob} blur-2xl rounded-full pointer-events-none`} />

                <div className="relative flex items-center gap-3">
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl glass-strong ${t.text}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/60">
                      Category {String(idx + 1).padStart(2, "0")}
                    </p>
                    <h3 className="font-display text-lg font-semibold text-white tracking-tight">
                      {cat.category}
                    </h3>
                  </div>
                </div>

                <div className="relative mt-6 flex flex-wrap gap-2">
                  {cat.items.map((item) => (
                    <span
                      key={item}
                      className={`font-mono text-xs px-3 py-1.5 rounded-md border ${t.chip} hover:bg-white/10 hover:text-white transition-colors duration-200`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
